import { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewListIcon from "@mui/icons-material/ViewList";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { acquireToken } from "../../authentication/utils/authUtils";
import { ScheduleCalendarView } from "./components/ScheduleCalendarView";
import { ScheduleRangeSelector } from "./components/ScheduleRangeSelector";
import { fetchMySchedule } from "./utils/myScheduleApi";
import {
  formatDate,
  getScheduleRange,
  getScheduleRangeDaysFromSearch,
  getScheduleRangeLabel,
  getScheduleRangeSearch,
  toApiDateTime,
} from "./utils/dateRangeUtils";

export const ScheduleCalendarPage = () => {
  const { instance } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  const account = instance.getActiveAccount();
  const accountKey = account?.homeAccountId || account?.username || "";
  const [selectedRangeDays, setSelectedRangeDays] = useState(() =>
    getScheduleRangeDaysFromSearch(location.search)
  );
  const [assignments, setAssignments] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const range = useMemo(
    () => getScheduleRange(selectedRangeDays),
    [selectedRangeDays]
  );
  const rangeLabel = getScheduleRangeLabel(selectedRangeDays);

  const handleRangeChange = (days) => {
    setSelectedRangeDays(days);
    navigate(
      {
        pathname: location.pathname,
        search: getScheduleRangeSearch(days),
      },
      { replace: true }
    );
  };

  const loadSchedule = async () => {
    if (!account) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = await acquireToken(instance, account);
      const result = await fetchMySchedule({
        token: token.accessToken,
        start: toApiDateTime(range.start),
        end: toApiDateTime(range.end),
      });

      setAssignments(result.value || []);
      setMeta(result.meta || null);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to load schedule";

      setError(message);
      setAssignments([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accountKey) {
      loadSchedule();
    } else {
      setLoading(false);
    }
  }, [accountKey, selectedRangeDays]);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        px: { xs: 1, md: 3 },
        py: { xs: 2, md: 3 },
        color: "white",
      }}
    >
      <Stack
        direction={{ xs: "column", lg: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", lg: "center" }}
        gap={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Schedule Calendar
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Calendar view for the next {rangeLabel}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatDate(range.start)} through {formatDate(range.end)}.
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1}
          sx={{ flexShrink: 0 }}
        >
          <ScheduleRangeSelector
            value={selectedRangeDays}
            onChange={handleRangeChange}
            disabled={loading}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadSchedule}
            disabled={loading}
            sx={{ flexShrink: 0 }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<ViewListIcon />}
            component={RouterLink}
            to={`/schedule${getScheduleRangeSearch(selectedRangeDays)}`}
            sx={{ flexShrink: 0 }}
          >
            List view
          </Button>
        </Stack>
      </Stack>

      {meta && (
        <Card sx={{ bgcolor: "#1A1A1A", color: "white", mb: 2 }}>
          <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
            <Typography variant="h6">
              {meta.totalReturned === 1
                ? "1 class found"
                : `${meta.totalReturned || assignments.length} classes found`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fetched {meta.totalFetched ?? assignments.length} scheduler assignments and matched {meta.totalReturned ?? assignments.length} to your coach account.
            </Typography>
          </CardContent>
        </Card>
      )}

      {loading && !meta && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && assignments.length === 0 && (
        <Alert severity="info">
          No scheduled classes were found for your coach account in the next {rangeLabel}.
          {selectedRangeDays < 30 ? " Try a longer range." : ""}
        </Alert>
      )}

      {!loading && !error && assignments.length > 0 && (
        <ScheduleCalendarView
          assignments={assignments}
          range={range}
          selectedRangeDays={selectedRangeDays}
          fullPage
        />
      )}
    </Box>
  );
};
